/**
 * ProfilePage — authenticated customer profile settings.
 *
 * Allows customers to view and edit their profile information.
 * Only first_name and last_name are editable; other fields are read-only.
 */
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, Check, Loader2, X, User, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/authStore'
import { customerAuthApi } from '../api/client'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { BRAND } from '../config'

interface FormState {
  first_name: string
  last_name: string
}

interface FormErrors {
  first_name?: string
  last_name?: string
}

interface InitialValues {
  first_name: string
  last_name: string
}

export function ProfilePage() {
  const navigate = useI18nNavigate()
  const { t } = useTranslation('dashboard')
  const { customer, loadCustomer } = useAuthStore()

  const [form, setForm] = useState<FormState>({ first_name: '', last_name: '' })
  const [initialValues, setInitialValues] = useState<InitialValues>({ first_name: '', last_name: '' })
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auth guard and initialize form
  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      navigate('/login')
      return
    }
    if (!customer) loadCustomer()
  }, [customer, loadCustomer, navigate])

  // Initialize form with customer data
  useEffect(() => {
    if (customer) {
      const initialFormValues = {
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
      }
      setForm(initialFormValues)
      setInitialValues(initialFormValues)
    }
  }, [customer])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!form.first_name.trim()) {
      newErrors.first_name = t('profile.validation.firstName', 'First name is required')
    }

    if (!form.last_name.trim()) {
      newErrors.last_name = t('profile.validation.lastName', 'Last name is required')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Check if form has been modified by comparing trimmed values
  const hasChanges = (): boolean => {
    return (
      form.first_name.trim() !== initialValues.first_name.trim() ||
      form.last_name.trim() !== initialValues.last_name.trim()
    )
  }

  // Check if form is valid (all required fields are non-empty after trimming)
  const isFormValid = (): boolean => {
    return form.first_name.trim().length > 0 && form.last_name.trim().length > 0
  }

  // Button should be enabled only if form has changes, is valid, and not loading
  const isSaveButtonEnabled = hasChanges() && isFormValid() && !loading

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    // Validate form
    if (!validateForm()) {
      return
    }

    // Check if there are actual changes before making API call
    if (!hasChanges()) {
      return
    }

    setLoading(true)
    try {
      await customerAuthApi.updateProfile({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      })

      // Refresh the customer store
      await loadCustomer()

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      setError(err.response?.data?.error || t('profile.error.update', 'Failed to update profile. Please try again.'))
    } finally {
      setLoading(false)
    }
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-[#fafaf8]">
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-[0px_4px_2px_rgba(0,0,0,0.08)]">
        <div className="mx-auto flex min-h-16 max-w-4xl items-center justify-between gap-4 px-5 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-500 transition-colors hover:text-gray-700 -ml-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-xs font-medium">{t('profile.back', 'Back')}</span>
          </button>
          <div className="flex-1" />
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto max-w-4xl px-5 py-8 sm:px-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <User className="h-7 w-7 text-gray-700" />
            <h1 className="text-3xl font-bold text-gray-900">{t('profile.title', 'Profile Settings')}</h1>
          </div>
          <p className="text-gray-500 text-sm">{t('profile.description', 'Manage your personal information and account details')}</p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-5">
              <User className="h-5 w-5 text-gray-600" />
              {t('profile.personalInfo', 'Personal Information')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* First Name */}
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.firstName', 'First Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="first_name"
                  type="text"
                  value={form.first_name}
                  onChange={(e) => {
                    setForm({ ...form, first_name: e.target.value })
                    if (errors.first_name) setErrors({ ...errors, first_name: undefined })
                  }}
                  disabled={loading}
                  className={`w-full rounded-lg border px-3.5 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    errors.first_name
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  } ${loading ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-900'}`}
                />
                {errors.first_name && <p className="mt-1.5 text-xs text-red-600">{errors.first_name}</p>}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.lastName', 'Last Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="last_name"
                  type="text"
                  value={form.last_name}
                  onChange={(e) => {
                    setForm({ ...form, last_name: e.target.value })
                    if (errors.last_name) setErrors({ ...errors, last_name: undefined })
                  }}
                  disabled={loading}
                  className={`w-full rounded-lg border px-3.5 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    errors.last_name
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  } ${loading ? 'bg-gray-50 text-gray-500' : 'bg-white text-gray-900'}`}
                />
                {errors.last_name && <p className="mt-1.5 text-xs text-red-600">{errors.last_name}</p>}
              </div>
            </div>
          </div>

          {/* Account Information Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-5">
              {t('profile.accountInfo', 'Account Information')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Email (read-only) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.email', 'Email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={customer.email || ''}
                  disabled
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>

              {/* Phone (read-only) */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.phone', 'Phone')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={customer.phone || ''}
                  disabled
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-400">{t('profile.readOnly', 'Read-only fields')}</p>
          </div>

          {/* Location Information Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-5">
              <MapPin className="h-5 w-5 text-gray-600" />
              {t('profile.locationInfo', 'Location Information')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Country Code (read-only) */}
              <div>
                <label htmlFor="country_code" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.countryCode', 'Country Code')}
                </label>
                <input
                  id="country_code"
                  type="text"
                  value={customer.country_code || ''}
                  disabled
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>

              {/* Country (read-only) */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.country', 'Country')}
                </label>
                <input
                  id="country"
                  type="text"
                  value={customer.country || ''}
                  disabled
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-400">{t('profile.readOnly', 'Read-only fields')}</p>
          </div>

          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-3"
            >
              <X className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Success message */}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700 flex items-start gap-3"
            >
              <Check className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{t('profile.success', 'Profile updated successfully!')}</span>
            </motion.div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              {t('profile.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={!isSaveButtonEnabled}
              style={{
                backgroundColor: !isSaveButtonEnabled ? '#d1d5db' : (loading ? '#999' : BRAND.red),
                cursor: !isSaveButtonEnabled ? 'not-allowed' : 'pointer',
              }}
              onMouseOver={(e) => {
                if (isSaveButtonEnabled && !loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.redHover
              }}
              onMouseOut={(e) => {
                if (isSaveButtonEnabled && !loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND.red
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? t('profile.saving', 'Saving...') : t('profile.save', 'Save Changes')}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}
